import React, { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import { CircularProgress, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { boxesOnPage, colorForField } from './fieldBoxes';
import styles from './DocumentBoxViewer.module.css';

const ZOOM_STEPS = [1, 1.25, 1.5, 2, 2.5, 3];
const SHOW_BOXES_STORAGE_KEY = 'documentBoxViewer.showBoxes';

const ensureWorker = () => {
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
        pdfjs.GlobalWorkerOptions.workerSrc =
            `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
    }
};

const escapeAttr = (value) =>
    (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));

const readShowBoxes = () => {
    try {
        return window.localStorage.getItem(SHOW_BOXES_STORAGE_KEY) !== 'false';
    } catch {
        return true;
    }
};

/** Any render failure inside the viewer hands control back to the plain preview. */
class ViewerErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error) {
        this.props.onError?.(error);
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

function FieldBoxes({ boxes, activeField, fieldOrder, fieldLabels, onFieldClick }) {
    return boxes.map((b) => {
        const isActive = activeField === b.field;
        const label = fieldLabels?.[b.field] || b.field;
        const className = [
            styles.box,
            isActive ? styles.boxActive : '',
            activeField && !isActive ? styles.boxDimmed : '',
        ].join(' ');
        return (
            <Tooltip key={`${b.field}-${b.index}`} title={label} placement="top" arrow disableInteractive>
                <button
                    type="button"
                    className={className}
                    data-field={b.field}
                    aria-label={label}
                    style={{
                        top: `${b.top}%`,
                        left: `${b.left}%`,
                        width: `${b.width}%`,
                        height: `${b.height}%`,
                        '--box-color': colorForField(b.field, fieldOrder),
                    }}
                    onClick={() => onFieldClick?.(b.field)}
                />
            </Tooltip>
        );
    });
}

function Viewer({
    url, isImage, positions, visibleFields, activeField, fieldOrder, fieldLabels, onFieldClick, onError, onOpenOriginal, onBoxesShownChange,
}) {
    const { t } = useTranslation();
    const scrollRef = useRef(null);
    const [numPages, setNumPages] = useState(0);
    const [width, setWidth] = useState(0);
    const [zoomIndex, setZoomIndex] = useState(0);
    const [showBoxes, setShowBoxes] = useState(readShowBoxes);

    ensureWorker();

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const measure = () => setWidth(Math.max(0, el.clientWidth - 16));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!activeField || !showBoxes || !scrollRef.current) return;
        const target = scrollRef.current.querySelector(`[data-field="${escapeAttr(activeField)}"]`);
        target?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
    }, [activeField, showBoxes, numPages]);

    const toggleBoxes = useCallback((_, checked) => {
        setShowBoxes(checked);
        try {
            window.localStorage.setItem(SHOW_BOXES_STORAGE_KEY, String(checked));
        } catch {
            // private mode: the toggle just isn't remembered
        }
    }, []);

    const zoom = ZOOM_STEPS[zoomIndex];
    const pageWidth = width > 0 ? Math.round(width * zoom) : undefined;
    // A different page count means this is not the file the boxes were drawn on.
    const hasBoxes = Boolean(positions) && numPages === positions.pageCount
        && (!visibleFields || visibleFields.size > 0);

    const pages = useMemo(() => Array.from({ length: numPages }, (_, i) => i + 1), [numPages]);

    const boxesShown = showBoxes && hasBoxes;
    useEffect(() => {
        onBoxesShownChange?.(boxesShown);
    }, [boxesShown, onBoxesShownChange]);
    useEffect(() => () => onBoxesShownChange?.(false), [onBoxesShownChange]);

    const renderBoxes = (pageNumber) => boxesShown && (
        <FieldBoxes
            boxes={boxesOnPage(positions, pageNumber, visibleFields)}
            activeField={activeField}
            fieldOrder={fieldOrder}
            fieldLabels={fieldLabels}
            onFieldClick={onFieldClick}
        />
    );

    return (
        <div className={styles.root}>
            <div className={styles.toolbar}>
                {hasBoxes && (
                    <FormControlLabel
                        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                        control={<Switch size="small" checked={showBoxes} onChange={toggleBoxes} />}
                        label={t('ShowFieldBoxes')}
                    />
                )}
                <span className={styles.toolbarSpacer} />
                <Tooltip title={t('ZoomOut')}>
                    <span>
                        <IconButton size="small" disabled={zoomIndex === 0} onClick={() => setZoomIndex((z) => Math.max(0, z - 1))}>
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
                <Tooltip title={t('ZoomIn')}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={zoomIndex === ZOOM_STEPS.length - 1}
                            onClick={() => setZoomIndex((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}
                        >
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {onOpenOriginal && (
                    <Tooltip title={t('OpenOriginalViewer')}>
                        <IconButton size="small" onClick={onOpenOriginal}>
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            <div className={styles.scroll} ref={scrollRef}>
                {isImage ? (
                    <div className={styles.pages}>
                        <div className={styles.page} data-page={1}>
                            <img
                                src={url}
                                alt=""
                                className={styles.image}
                                style={pageWidth ? { width: pageWidth } : undefined}
                                onLoad={() => setNumPages(1)}
                                onError={onError}
                            />
                            {renderBoxes(1)}
                        </div>
                    </div>
                ) : (
                    <Document
                        file={url}
                        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                        onLoadError={onError}
                        onSourceError={onError}
                        loading={<div className={styles.status}><CircularProgress size={24} /></div>}
                        error={<div className={styles.status} />}
                        className={styles.pages}
                    >
                        {pages.map((pageNumber) => (
                            <div key={pageNumber} className={styles.page} data-page={pageNumber}>
                                <Page
                                    pageNumber={pageNumber}
                                    width={pageWidth}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    onRenderError={onError}
                                    loading={null}
                                />
                                {renderBoxes(pageNumber)}
                            </div>
                        ))}
                    </Document>
                )}
            </div>
        </div>
    );
}

/**
 * PDF preview with the OCR field boxes on top. Boxes are percentages of each rendered page, so
 * they follow zoom and resizing. Calls onError on any load / render failure so the parent can show
 * its regular preview instead.
 */
export default function DocumentBoxViewer(props) {
    const { onError } = props;
    const handleError = useCallback((error) => onError?.(error), [onError]);
    return (
        <ViewerErrorBoundary onError={handleError}>
            <Viewer {...props} onError={handleError} />
        </ViewerErrorBoundary>
    );
}

DocumentBoxViewer.propTypes = {
    /** PDF URL (presigned) or object URL. */
    url: PropTypes.string.isRequired,
    /** The URL is a single image (PNG / JPEG) rather than a PDF; boxes go on page 1. */
    isImage: PropTypes.bool,
    /** Output of normalizeFieldPositions; null shows the document without boxes. */
    positions: PropTypes.shape({
        pageCount: PropTypes.number.isRequired,
        fields: PropTypes.object.isRequired,
    }),
    /** Fields allowed to show (see visibleFieldSet); all fields when omitted. */
    visibleFields: PropTypes.instanceOf(Set),
    /** Field to emphasise and scroll to (e.g. the focused input). */
    activeField: PropTypes.string,
    /** Field order for colors, so each field keeps its color between documents. */
    fieldOrder: PropTypes.arrayOf(PropTypes.string),
    /** Display name per field (tooltip / aria-label). */
    fieldLabels: PropTypes.object,
    onFieldClick: PropTypes.func,
    onError: PropTypes.func,
    /** Shown as a toolbar button when provided (switch back to the browser's PDF viewer). */
    onOpenOriginal: PropTypes.func,
    /** Called with true while boxes are drawn on the pages, false otherwise (also on unmount). */
    onBoxesShownChange: PropTypes.func,
};
